import React, { useState, useRef, useMemo } from "react";
import "assets/css/style.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import TableSkeleton from "components/ui/TableSkeleton";
import SearchSelect from "components/ui/SearchSelect";
import Button from "components/ui/Button";
import { showToast } from "components/ui/toast";
import {
  useAmlQuery,
  useAmlByIdQuery,
  fetchAmlReportUrl,
  useReplaceAmlReportMutation,
} from "apis/aml";

// ── label maps ──
const VERIFIED_LABELS = {
  verified: "Verified",
  inProgress: "In Progress",
  notVerified: "Not Verified",
  notStarted: "Not Started",
};

const VERIFIED_COLORS = {
  verified: "bg-green-100 text-green-800",
  inProgress: "bg-yellow-100 text-yellow-800",
  notVerified: "bg-red-100 text-red-800",
  notStarted: "bg-lightgray text-darkgray",
};

const RISK_COLORS = {
  Low: "bg-lightgray text-darkgray",
  Medium: "bg-yellow-100 text-yellow-800",
  High: "bg-red-100 text-red-800",
};

const RISK_OPTIONS = [
  { value: "", label: "All Risk Levels" },
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

// 100 is the server's hard cap on pageSize — asking for more silently clamps.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const complianceColor = (value) => {
  const v = (value || "").toLowerCase();
  if (["clear", "verified", "approved"].includes(v))
    return "bg-green-100 text-green-800";
  if (["match", "failed", "rejected"].includes(v))
    return "bg-red-100 text-red-800";
  if (["inprogress", "pending", "requested", "submitted"].includes(v))
    return "bg-yellow-100 text-yellow-800";
  return "bg-lightgray text-darkgray";
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "-";

const riskText = (level) => {
  if (!level) return "Not yet assessed";
  const v = String(level).toLowerCase();
  if (v === "unknown" || v === "none" || v === "null")
    return "Not yet assessed";
  return level;
};

// ── small presentational bits ──

const VerifiedBadge = ({ bucket, label }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      VERIFIED_COLORS[bucket] || VERIFIED_COLORS.notStarted
    }`}
  >
    {label || VERIFIED_LABELS[bucket] || "Not Started"}
  </span>
);

const RiskPill = ({ level }) => {
  const v = String(level || "").toLowerCase();
  if (!["low", "medium", "high"].includes(v)) {
    return <span className="text-darkgray">-</span>;
  }
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        RISK_COLORS[level] || RISK_COLORS.Low
      }`}
    >
      {level}
    </span>
  );
};

const ComplianceChip = ({ value }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${complianceColor(
      value,
    )}`}
  >
    {value || "Not Started"}
  </span>
);

const ReportDownloadButton = ({ id }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      const url = await fetchAmlReportUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      // no-op
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      className="outline-button text-xs px-2 py-1"
      loading={loading}
      onClick={handleClick}
    >
      Download
    </Button>
  );
};

// Upload a replacement PDF over the existing report. The file overwrites the
// same S3 key, so any link already shared resolves to the new document — which
// is the whole point, and also why it confirms before uploading.
//
// The confirm step is an inline panel rather than a Modal: this button also
// lives inside the detail Modal, and Modal closes on any click outside its own
// box, so a nested one would dismiss the detail modal underneath it.
const ReplaceReportButton = ({ id, onReplaced }) => {
  const inputRef = useRef(null);
  const [pending, setPending] = useState(null);
  const { mutate, isPending } = useReplaceAmlReportMutation();

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same file twice still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      showToast("error", "The replacement report must be a PDF");
      return;
    }
    setPending(file);
  };

  const handleConfirm = () => {
    if (!pending) return;
    mutate(
      { id, file: pending },
      {
        onSuccess: () => {
          setPending(null);
          onReplaced?.();
        },
      },
    );
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handlePick}
      />

      {!pending ? (
        <Button
          className="outline-button text-xs px-2 py-1"
          onClick={() => inputRef.current?.click()}
        >
          Replace
        </Button>
      ) : (
        <div className="border border-mediumgray rounded-lg p-3 bg-white">
          <div className="text-sm text-black mb-1 break-all">
            Replace with <span className="font-medium">{pending.name}</span>?
          </div>
          <div className="text-xs text-darkgray mb-3">
            The new PDF is stored in place of the current one, so anyone who
            already has a link will see the replacement. The old file cannot be
            restored from here.
          </div>
          <div className="flex items-center gap-2">
            <Button
              className="black-button text-xs px-3 py-1.5"
              loading={isPending}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
            <Button
              className="outline-button text-xs px-3 py-1.5"
              onClick={() => setPending(null)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

const CountCard = ({ label, value, dotClass }) => (
  <div className="border border-mediumgray rounded-xl p-4 sm:p-5 bg-white">
    <div className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-darkgray">
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotClass}`} />
      <span className="truncate">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-bold mt-2 text-black">
      {value}
    </div>
  </div>
);

// ── detail modal content ──

const DetailField = ({ label, value }) => (
  <div className="mb-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
      {label}
    </div>
    <div className="text-sm text-black">{value || "-"}</div>
  </div>
);

const PartyCard = ({ party }) => (
  <div className="border border-mediumgray rounded-xl p-4 mb-3">
    <div className="text-base font-bold text-black">{party.name}</div>
    <div className="text-sm text-darkgray mb-3">{party.roleLabel || "-"}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          ID Verification
        </div>
        <ComplianceChip value={party.idVerification} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          PEP Screening
        </div>
        <ComplianceChip value={party.pepScreening} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          Sanctions
        </div>
        <ComplianceChip value={party.sanctions} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          SOF / SOW
        </div>
        <ComplianceChip value={party.sofSow} />
      </div>
    </div>
  </div>
);

const AmlDetailSkeleton = () => (
  <div>
    <div className="mb-5">
      <Skeleton width={110} height={24} borderRadius={999} />
    </div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="mb-4" key={i}>
        <Skeleton width={120} height={10} />
        <Skeleton width="60%" height={16} />
      </div>
    ))}
    <div className="border-t border-mediumgray mt-5 pt-5">
      <Skeleton width={160} height={18} className="mb-3" />
      <div className="border border-mediumgray rounded-xl p-4">
        <Skeleton width={140} height={18} />
        <Skeleton width={80} height={12} className="mb-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <Skeleton width={90} height={10} />
              <Skeleton height={24} borderRadius={999} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const AmlDetail = ({ id }) => {
  const { data: detail, isPending } = useAmlByIdQuery(id);

  if (isPending) return <AmlDetailSkeleton />;
  if (!detail) return <p className="py-4">No detail available.</p>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <VerifiedBadge bucket={detail.verified} label={detail.verifiedLabel} />
      </div>

      <DetailField label="Property Address" value={detail.address} />
      <DetailField label="Date Created" value={fmtDate(detail.dateCreated)} />
      <DetailField label="Agency Office" value={detail.agencyOffice} />
      <DetailField label="Agent" value={detail.agent} />
      <DetailField label="Ownership Type" value={detail.ownershipType} />
      <DetailField
        label="Purpose of Transaction"
        value={detail.purposeOfTransaction}
      />
      <DetailField label="Status" value={detail.dealStatusLabel} />
      <DetailField label="Risk Level" value={riskText(detail.riskLevel)} />

      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          Compliance Report
        </div>
        {!detail.hasReport && (
          <div className="text-sm text-darkgray mb-2">
            Not generated yet — uploading one here creates it.
          </div>
        )}
        <div className="flex flex-wrap items-start gap-2">
          {detail.hasReport && <ReportDownloadButton id={detail.id} />}
          <ReplaceReportButton id={detail.id} />
        </div>
        {detail.reportReplacedAt && (
          <div className="text-xs text-darkgray mt-1.5">
            Replaced manually on {fmtDate(detail.reportReplacedAt)}
          </div>
        )}
      </div>

      <div className="border-t border-mediumgray mt-5 pt-5">
        <div className="text-base font-bold mb-3 text-black">
          Party Compliance ({detail.parties?.length || 0})
        </div>
        {(detail.parties || []).map((p, i) => (
          <PartyCard key={p.individualId || i} party={p} />
        ))}
        {(!detail.parties || detail.parties.length === 0) && (
          <p className="text-darkgray">No parties on this deal yet.</p>
        )}
      </div>

      {detail.lastError && (
        <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm mt-4">
          Last error: {detail.lastError}
        </div>
      )}
    </div>
  );
};

// ── filter bar (module-level, not recreated each render) ──

const FilterBar = ({ tab, onTabChange, riskOption, onRiskChange }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full mb-4">
    <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
      {[
        { key: "", label: "All" },
        { key: "verified", label: "Verified" },
        { key: "inProgress", label: "In Progress" },
        { key: "notVerified", label: "Not Verified" },
        { key: "notStarted", label: "Not Started" },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => onTabChange(t.key)}
          className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap shrink-0 ${
            tab === t.key ? "bg-black text-white" : "bg-lightgray text-darkgray"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>

    <div className="w-full sm:w-44 sm:ml-auto shrink-0">
      <SearchSelect
        options={RISK_OPTIONS}
        value={riskOption}
        onChange={(opt) => onRiskChange(opt || RISK_OPTIONS[0])}
        searchable={false}
        placeholder="All Risk Levels"
      />
    </div>
  </div>
);

// ── main page ──

const Aml = () => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("");
  const [riskOption, setRiskOption] = useState(RISK_OPTIONS[0]);
  // Search text arrives from the table already debounced and trimmed.
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      status: tab || undefined,
      riskLevel: riskOption?.value || undefined,
      search: search || undefined,
      page,
      pageSize,
    }),
    [tab, riskOption, search, page, pageSize],
  );

  // Filter changes must reset to page 1, or you land on an out-of-range page.
  const handleTabChange = (key) => {
    setTab(key);
    setPage(1);
  };
  const handleRiskChange = (opt) => {
    setRiskOption(opt || RISK_OPTIONS[0]);
    setPage(1);
  };
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };
  const handleSearchChange = (query) => {
    setSearch(query);
    setPage(1);
  };

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useAmlQuery(params);

  const counts = data?.counts || {};
  const rows = data?.data || [];
  const pagination = data?.pagination || null;

  const columns = useMemo(
    () => [
      {
        accessorKey: "dateCreated",
        header: "Date Created",
        cell: (row) => (
          <span className="text-sm">{fmtDate(row.dateCreated)}</span>
        ),
        isFilter: false,
        minWidth: "140px",
      },
      {
        accessorKey: "address",
        header: "Property Address",
        cell: (row) => <span className="font-medium">{row.address}</span>,
        isFilter: true,
        minWidth: "220px",
      },
      {
        accessorKey: "agencyOffice",
        header: "Agency Office",
        cell: (row) => (
          <span className="text-sm text-darkgray">
            {row.agencyOffice || "-"}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "agent",
        header: "Agent",
        cell: (row) => (
          <span className="text-sm text-darkgray">{row.agent || "-"}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "verified",
        header: "Verified",
        cell: (row) => (
          <VerifiedBadge bucket={row.verified} label={row.verifiedLabel} />
        ),
        isFilter: false,
      },
      {
        accessorKey: "riskLevel",
        header: "Risk",
        cell: (row) => <RiskPill level={row.riskLevel} />,
        isFilter: false,
      },
      {
        accessorKey: "report",
        header: "Report",
        // Download only. Replacing is done from the detail modal, where the
        // confirm step has room and the deal it belongs to is on screen.
        cell: (row) =>
          row.hasReport ? (
            <ReportDownloadButton id={row.id} />
          ) : (
            <span className="text-darkgray text-xs">-</span>
          ),
        isFilter: false,
        minWidth: "120px",
      },
    ],
    [],
  );

  if (isDataError) {
    return (
      <div className="container mx-auto px-4 sm:px-0">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading AML dashboard: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const handleRowClick = (row) => {
    setSelectedId(row.id);
    setIsViewOpen(true);
  };

  const selectedAddress =
    rows.find((r) => r.id === selectedId)?.address || "Campaign Details";

  return (
    <>
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={selectedAddress}
        className="max-w-2xl"
      >
        {selectedId && <AmlDetail id={selectedId} />}
      </Modal>

      <div className="container mx-auto px-4 sm:px-0">
        {/* Header */}
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-wider text-black">
            AML Dashboard
          </div>
          <div className="text-darkgray mt-1">
            Track property campaign verification status across your organisation
          </div>
        </div>

        {/* Count cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <CountCard
            label="Verified"
            value={counts.verified ?? "-"}
            dotClass="bg-green-500"
          />
          <CountCard
            label="In Progress"
            value={counts.inProgress ?? "-"}
            dotClass="bg-yellow-500"
          />
          <CountCard
            label="Not Verified"
            value={counts.notVerified ?? "-"}
            dotClass="bg-red-500"
          />
          <CountCard
            label="Not Started"
            value={counts.notStarted ?? "-"}
            dotClass="bg-darkgray"
          />
        </div>

        <div>
          <h4 className="mb-4">Campaigns</h4>
          <FilterBar
            tab={tab}
            onTabChange={handleTabChange}
            riskOption={riskOption}
            onRiskChange={handleRiskChange}
          />
          {isDataPending ? (
            <TableSkeleton
              rows={Math.min(pageSize, 10)}
              columns={columns.length}
            />
          ) : (
            <Table
              columns={columns}
              data={rows}
              pageSize={pageSize}
              onRowClick={handleRowClick}
              searchPlaceholder="Search property address, agent or office..."
              onSearchChange={handleSearchChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageSizeChange={handlePageSizeChange}
              emptyText={
                search ? "No campaigns match your search" : "No campaigns found"
              }
              serverPagination={
                pagination
                  ? {
                      page: pagination.page,
                      totalPages: pagination.totalPages,
                      total: pagination.total,
                      limit: pagination.pageSize,
                    }
                  : null
              }
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Aml;
